/**
 * Main entry point for IdP JavaScript
 * ES Module architecture
 */

// 탭 틀 안 모드 (spec 2026-09-29 §5.3) — **첫 import** 여야 한다: 다른 모듈보다 먼저 history 를 잡는다
// (server/workspace_test.go 가 지킨다).
import { setupWorkspaceFrame, WRAPPING } from './modules/workspace-frame.js';

// Import all modules
import { IdPClient } from './modules/idp-client.js';
import * as utils from './modules/utils.js';
import { setupPasswordStrength } from './modules/user-management.js';
import { setupGroupModals } from './modules/groups.js';
import { setupResponsiveTables } from './modules/mobile.js';
import { setupAttendanceClock } from './modules/attendance.js';
import { setupAttendanceLive } from './modules/attendance-live.js';
import { setupAttendanceRequestModal } from './modules/attendance-request.js';
import { setupAttendanceApproval } from './modules/attendance-approval.js';
import { setupNotificationCenter, setupPushQuick } from './modules/notification-center.js';
import { setupCalendar } from './modules/calendar.js';
import { setupCalendarHistory } from './modules/calendar-history.js';
import { setupCalendarGrid } from './modules/calendar-grid.js';
import { setupCalendarAgenda } from './modules/calendar-agenda.js';
import { setupDashboardQuickLinks } from './modules/dashboard-quicklinks.js';
import { setupProjectTimeline } from './modules/project-timeline.js';
import { setupProjectList } from './modules/project-list.js';
import { setupCalendarLive } from './modules/calendar-live.js';
import { setupCalendarList } from './modules/calendar-list.js';
import { setupCalendarViews } from './modules/calendar-views.js';
import { setupCalendarBoard } from './modules/calendar-board.js';
import { setupCalendarTimeGrid } from './modules/calendar-timegrid.js';
import { setupPropManager } from './modules/calendar-props.js';
import { setupCalendarTrash } from './modules/calendar-trash.js';
import { setupCalendarSpa, setAfterSwapHook } from './modules/calendar-spa.js';
import { setupCalendarContextMenu } from './modules/calendar-context.js';
import { setupCRM } from './modules/crm.js';
import { setupERP } from './modules/erp.js';
import { setupWork } from './modules/work.js';
import { setupWorkInbox } from './modules/work-inbox.js';
import { setupHROperations } from './modules/hr-operations.js';
import { setupAppLauncher } from './modules/apps.js';
import { initVaultSearch } from './modules/vault-search.js';
import { initSelectPanels } from './modules/select-panel.js';
import { initConfirmDialogs } from './modules/confirm-dialog.js';
import { setupModalBackdropGuard } from './modules/modal-backdrop-guard.js';
import { setupFileViewer } from './modules/file-viewer.js';
import { initFormUX } from './modules/form-ux.js';
import { initRailTooltips, closeAdminDrawer } from './modules/rail-tooltip.js';
import { setupMobileShell } from './modules/mobile-shell.js';
import { setupPwa } from './modules/pwa.js';
import { setupBackLink } from './modules/back-link.js';
import { setupGlobalSidebar } from './modules/global-sidebar.js';
import { setupOnboarding } from './modules/onboarding.js';

// Import modules for side effects (they register global functions)
import './modules/language.js';
import './modules/user-menu.js';
import './modules/totp.js';
import './modules/notifications.js';
import './modules/user-management.js';
import './modules/groups.js';
import './modules/mobile.js';
import './modules/oauth2.js';
import './modules/nav-more.js'; // 헤더 탭 줄의 「더보기 ▾」 메뉴 (문서 위임 — SPA 교체에 살아남는다)

// ── 관리자 화면(/admin/*) 페이지 초기화 등록부 ──────────────────────────────────
// 관리자 화면은 calendar-spa.js 가 제자리 교체(main.admin-main 통째 교체)로 이동한다.
// 교체된 DOM 안의 인라인 <script> 는 다시 실행되지 않으므로, 페이지 동작은 모듈로
// 옮기고 여기에 등록한다.
//
// 계약 (P1/P2/P3 공통):
//   1. modules/admin-<page>.js 가 `export function setup<Page>(root, { signal })` 를 내보낸다.
//   2. 여기에 import 한 줄 + registerAdminPage('<Page 키>', setup<Page>) 한 줄을 더한다.
//   - '<Page 키>' 는 서버가 넘기는 data["Page"] 값 (= <body data-page>).
//   - root   = main.admin-main (교체 대상 컨테이너). 모든 조회는 root 기준으로 한다
//              — document 전역 조회는 교체 도중 옛 노드를 잡을 수 있다.
//   - signal = 이 화면에만 유효한 AbortSignal. 리스너는 전부 { signal } 로 붙인다.
//              다음 화면으로 넘어가면 abort 되므로 중복이 쌓이지 않는다.
//   - setup 은 최초 로드와 제자리 교체(calendar:page-swapped)마다 호출된다 — 여러 번 불려도
//     안전해야 한다.
//   - document/window 리스너가 꼭 필요하면 { signal } 을 붙이거나 모듈 안에서 한 번만
//     붙도록 (wireOnce 패턴) 가드한다.
//   - i18n 문구·서버 데이터는 교체되는 root 안의 data-* 섬에서 읽는다.
const adminPages = new Map();
let adminPageAbort = null;

function registerAdminPage(pageKey, loadSetup) {
    adminPages.set(pageKey, loadSetup);
}

// P2 pages (notifiers/oauth2/ldap-password/api-keys/login-attempts/
// ip-blacklist/saml/external-users/user-invites/messages/totp-setup/vault)
registerAdminPage('admin_notifiers', () => import('./modules/admin-notifiers.js').then((m) => m.setupAdminNotifiers));
registerAdminPage('admin_oauth2_clients', () => import('./modules/admin-oauth2-clients.js').then((m) => m.setupAdminOAuth2Clients));
registerAdminPage('admin_ldap_password_reset', () => import('./modules/admin-ldap-password-reset.js').then((m) => m.setupAdminLdapPasswordReset));
registerAdminPage('admin_api_keys', () => import('./modules/admin-api-keys.js').then((m) => m.setupAdminApiKeys));
registerAdminPage('admin_ip_blacklist', () => import('./modules/admin-ip-blacklist.js').then((m) => m.setupAdminIpBlacklist));
registerAdminPage('admin_saml_providers', () => import('./modules/admin-saml-providers.js').then((m) => m.setupAdminSamlProviders));
registerAdminPage('admin_scim_provisioning', () => import('./modules/admin-scim-provisioning.js').then((m) => m.setupAdminScimProvisioning));
registerAdminPage('external_users', () => import('./modules/admin-external-users.js').then((m) => m.setupAdminExternalUsers));
registerAdminPage('admin_user_invites', () => import('./modules/admin-user-invites.js').then((m) => m.setupAdminUserInvites));
registerAdminPage('admin_user_invite_detail', () => import('./modules/admin-user-invites.js').then((m) => m.setupAdminUserInvites));
registerAdminPage('admin_messages', () => import('./modules/admin-messages.js').then((m) => m.setupAdminMessages));
registerAdminPage('admin_totp_setup', () => import('./modules/admin-totp-setup.js').then((m) => m.setupAdminTotpSetup));
// MCP 점검 (/admin/mcp): 도구 목록·호출·빠른 점검 — 서버 프록시만 부른다.
registerAdminPage('admin_mcp', () => import('./modules/admin-mcp.js').then((m) => m.setupAdminMcp));
// 인사팀 데이터 이관 (/admin/hrga, HRGA P6): 파일 미리보기 조각 · 사람 대응 · 적용.
registerAdminPage('admin_hrga', () => import('./modules/admin-hrga.js').then((m) => m.setupAdminHRGA));
// /admin/vault/totp 은 등록하지 않는다: calendar-spa.js 의 SECTION_EXCLUDE_PREFIXES 에 있어
// 제자리로 도착할 일이 없고(항상 통째 로드라 페이지 자체 <script> 가 그대로 돈다),
// 예전의 새로고침 폴백은 지연 로드와 만나면 무한 새로고침이 된다 (모듈이 도착할 때쯤엔
// document.readyState 가 이미 'complete' 다).
registerAdminPage('admin_cred_vault', () => import('./modules/admin-cred-vault.js').then((m) => m.setupAdminCredVault));

// P3 pages (attendance-report/attendance-user/approval-forms/appointments).
// ranks/duties belong to P1 (see the P1 section in tests/e2e/admin-spa.mjs).
// attendance-records/attendance-requests/attendance-leave have no
// page-specific JS —
// data-toggle-fetch (form-ux.js) and data-confirm (confirm-dialog.js)
// are already document-delegated, so they need no registration here.
// approval-docs (결재 문서 관리) is the user boxes' rows + modal since 백로그 AW2,
// so it runs the same approval-box.js.
registerAdminPage('admin_attendance_settings', () => import('./modules/admin-attendance-settings.js').then((m) => m.setupAdminAttendanceSettings));
// /admin/holidays: the same holiday card (partials/holidays-card.html), so the same module.
registerAdminPage('admin_holidays', () => import('./modules/admin-attendance-settings.js').then((m) => m.setupAdminAttendanceSettings));
// /admin/activity (활동 요약): 목록+폼과 상세가 한 모듈이다.
registerAdminPage('admin_activity', () => import('./modules/admin-activity.js').then((m) => m.setupAdminActivity));
registerAdminPage('admin_activity_review', () => import('./modules/admin-activity.js').then((m) => m.setupAdminActivity));
registerAdminPage('admin_attendance_report', () => import('./modules/admin-attendance-report.js').then((m) => m.setupAdminAttendanceReport));
registerAdminPage('admin_attendance_user', () => import('./modules/admin-attendance-user.js').then((m) => m.setupAdminAttendanceUser));
// /admin/attendance/workplaces/:id — 출퇴근 위치 카드 (지도 · 주소 검색 · 현재 위치, 2026-10-02).
registerAdminPage('admin_attendance_workplace', () => import('./modules/admin-attendance-workplace.js').then((m) => m.setupAdminAttendanceWorkplace));
registerAdminPage('admin_approval_forms', () => import('./modules/admin-approval-forms.js').then((m) => m.setupAdminApprovalForms));
registerAdminPage('admin_approval_docs', () => import('./modules/approval-box.js').then((m) => m.setupApprovalBox));
registerAdminPage('admin-appointments', () => import('./modules/admin-appointments.js').then((m) => m.setupAdminAppointments));

// P1 pages (사용자 목록·부서·그룹·역할·권한·직급·직무·시스템 설정·바로가기·사용자 추가/편집).
// deleted-users/group-sync/apps 는 페이지 전용 JS 가 없다 (네이티브 confirm 과 폼 제출뿐).
registerAdminPage('admin', () => import('./modules/admin-users.js').then((m) => m.setupAdminUsers));
registerAdminPage('admin_groups', () => import('./modules/admin-groups.js').then((m) => m.setupAdminGroups));
registerAdminPage('admin-departments', () => import('./modules/admin-departments.js').then((m) => m.setupAdminDepartments));
registerAdminPage('admin_roles', () => import('./modules/admin-roles.js').then((m) => m.setupAdminRoles));
registerAdminPage('admin_role_edit', () => import('./modules/admin-role-edit.js').then((m) => m.setupAdminRoleEdit));
registerAdminPage('admin_permissions', () => import('./modules/admin-permissions.js').then((m) => m.setupAdminPermissions));
registerAdminPage('admin-ranks', () => import('./modules/admin-ranks.js').then((m) => m.setupAdminRanks));
registerAdminPage('admin-duties', () => import('./modules/admin-duties.js').then((m) => m.setupAdminDuties));
registerAdminPage('admin_system_config', () => import('./modules/admin-system-config.js').then((m) => m.setupAdminSystemConfig));
registerAdminPage('admin_quicklinks', () => import('./modules/admin-quicklinks.js').then((m) => m.setupAdminQuickLinks));
registerAdminPage('admin-user-add', () => import('./modules/admin-user-add.js').then((m) => m.setupAdminUserAdd));
registerAdminPage('admin-user-edit', () => import('./modules/admin-user-edit.js').then((m) => m.setupAdminUserEdit));

// 업무 평가 (/evaluation/*, E-18 §3.2): 관리자 화면과 같은 계약이다 — 첫 로드와
// 제자리 교체마다 setupEvaluationPage(main, { signal }) 가 붙고 앞 화면의 signal 은
// abort 된다. 여덟 화면이 한 모듈이다.
registerAdminPage('evaluation_list', () => import('./modules/evaluation.js').then((m) => m.setupEvaluationPage));
registerAdminPage('evaluation_me', () => import('./modules/evaluation.js').then((m) => m.setupEvaluationPage));
registerAdminPage('evaluation_subject', () => import('./modules/evaluation.js').then((m) => m.setupEvaluationPage));
registerAdminPage('evaluation_rubrics', () => import('./modules/evaluation.js').then((m) => m.setupEvaluationPage));
registerAdminPage('evaluation_rubric', () => import('./modules/evaluation.js').then((m) => m.setupEvaluationPage));
registerAdminPage('evaluation_auto', () => import('./modules/evaluation.js').then((m) => m.setupEvaluationPage));
registerAdminPage('evaluation_feedback', () => import('./modules/evaluation.js').then((m) => m.setupEvaluationPage));
registerAdminPage('evaluation_new', () => import('./modules/evaluation.js').then((m) => m.setupEvaluationPage));
registerAdminPage('evaluation_show', () => import('./modules/evaluation.js').then((m) => m.setupEvaluationPage));

// 지금 화면의 모듈이 붙을 때까지 기다릴 수 있게 마지막 작업을 들고 있는다.
let adminPagePending = Promise.resolve();

function runAdminPage() {
    if (adminPageAbort) { adminPageAbort.abort(); adminPageAbort = null; } // 앞 화면의 리스너를 한 번에 뗀다
    const loadSetup = adminPages.get(document.body.dataset.page || '');
    const root = document.querySelector('main.admin-main');
    if (!loadSetup || !root) { adminPagePending = Promise.resolve(); return adminPagePending; }
    const controller = new AbortController();
    adminPageAbort = controller;
    adminPagePending = Promise.resolve()
        .then(loadSetup)
        .then((setup) => {
            // 모듈을 받아 오는 동안 다음 화면으로 넘어갔다면 이 화면에는 더 붙이지 않는다
            if (controller.signal.aborted || typeof setup !== 'function') return;
            setup(root, { signal: controller.signal });
        })
        .catch((err) => { console.error('admin page setup failed', err); });
    return adminPagePending;
}

// 제자리 교체는 이 모듈이 붙은 뒤에야 "끝난" 것이다: calendar-spa.js 가 진행 표시를
// 끄고 calendar:navigated 를 보내기 전에 여기서 기다린다 (테스트의 spaSettled 도 이걸 탄다).
setAfterSwapHook(() => adminPagePending);


// Initialize application when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    // 틀 밖의 데스크톱 설치 앱 창은 /workspace 로 옮겨 가는 중이다 — 이 문서를 차리지 않는다.
    if (WRAPPING) return;
    // Create main client instance
    const client = new IdPClient();

    // Setup page-specific features
    setupPasswordStrength();
    setupGroupModals();
    setupResponsiveTables();
    setupAttendanceClock();
    setupAttendanceLive();
    setupAttendanceRequestModal();
    setupAttendanceApproval();
    setupNotificationCenter();
    setupPushQuick();
    // 통합검색 (HRGA P6): 머리줄 단추·패널·/ 단축키. 벨과 같은 자리 — 머리줄은 SPA 교체에도 남으므로
    // 한 번만 붙고(data-search-ready), 단추가 없는 화면(검색이 닫힌 사람)에서는 싣지 않는다.
    if (document.getElementById('hdrSearch')) import('./modules/search.js').then((m) => m.setupSearch());
    setupCalendar();
    setupCalendarHistory(); // undo/redo stack + Ctrl+Z (calendar pages only)
    setupCalendarGrid(); // 월 그리드: 빠른 추가·다중 선택·드래그
    setupCalendarAgenda(); // 모바일 월·주의 그날 목록·스와이프·FAB (no-op elsewhere)
    setupDashboardQuickLinks(); // 대시보드 바로가기 편집 (no-op elsewhere)
    setupProjectTimeline();
    setupProjectList(); // 프로젝트 허브 목록: 인라인 편집·계층·다중 선택 (no-op elsewhere)
    setupCalendarLive(); // 다른 사용자의 변경을 SSE 로 조용히 반영 (no-op elsewhere)
    setupCalendarList(); // /calendar 표 뷰: 선택·상태·키보드·인라인 추가 (no-op elsewhere)
    setupCalendarViews(); // 캘린더 뷰 탭·조건 툴바·저장/되돌리기 (문서 위임, no-op elsewhere)
    setupCalendarBoard(); // 캘린더 보드 뷰: 드래그·열 바닥 추가 (no-op elsewhere)
    setupCalendarTimeGrid(); // 시간표 뷰: 시간 축 끌기·블록 이동·끝 시각 (no-op elsewhere)
    setupPropManager(); // 캘린더 속성 관리자 (허브·오버레이 공용, 문서 위임)
    setupCalendarTrash(); // 휴지통 페이지 (/calendar/trash) — 없는 화면에서는 no-op
    setupCalendarSpa(); // 같은 페이지 안의 이동은 새로고침 없이 (no-op elsewhere)
    setupCalendarContextMenu(); // 빈 곳·패널 우클릭 메뉴
    setupCRM(); // 고객 관리 (no-op elsewhere)
    setupERP(); // 경영 관리 (no-op elsewhere)
    setupHROperations();
    // 인력정보 편집기 (HRGA §6.3): 첫 로드와 SPA 도착 모두에서 붙는다 (data-bound 로 한 번만)
    const hrWorkforce = () => { if (document.getElementById('hrWorkforce')) import('./modules/hr-workforce.js').then((m) => m.setupHRWorkforce()); };
    hrWorkforce();
    document.addEventListener('calendar:navigated', hrWorkforce);
    // 프로젝트 참여율 격자 (HRGA §7.3): 첫 로드와 SPA 도착 모두에서 붙는다 (data-bound 로 한 번만)
    const part = () => { if (document.getElementById('partPage')) import('./modules/participation.js').then((m) => m.setupParticipation()); };
    part();
    document.addEventListener('calendar:navigated', part);
    // 자산관리 표 (HRGA §8.3): 첫 로드와 SPA 도착·선택자 교체 뒤 모두 (data-bound 로 한 번만 붙는다)
    const gaAssets = () => { if (document.getElementById('gaPage')) import('./modules/assets.js').then((m) => m.setupAssets()); };
    gaAssets();
    document.addEventListener('calendar:navigated', gaAssets);
    // 자리배치 (HRGA §9.3): 첫 로드와 SPA 도착·같은 화면 다시 받기 뒤 모두 (data-bound 로 한 번만 붙는다)
    const seats = () => { if (document.getElementById('seatsPage')) import('./modules/seats.js').then((m) => m.setupSeats()); };
    seats();
    document.addEventListener('calendar:navigated', seats);
    // 조직도 편집 (HRGA §10): 첫 로드와 SPA 도착·같은 화면 다시 받기 뒤 모두 (data-bound 로 한 번만 붙는다)
    const orgchart = () => { if (document.getElementById('orgPage')) import('./modules/orgchart.js').then((m) => m.setupOrgChart()); };
    orgchart();
    document.addEventListener('calendar:navigated', orgchart);
    // 확인 필요 (HRGA P6): 첫 로드와 SPA 도착 모두에서 붙는다 (data-bound 로 한 번만)
    const hrIssues = () => { if (document.getElementById('hrIssues')) import('./modules/hr-issues.js').then((m) => m.setupHRIssues()); };
    hrIssues();
    document.addEventListener('calendar:navigated', hrIssues);
    // 프로필 자리배치 탭 (HRGA §9.4): 첫 로드와 SPA 도착 모두에서 붙는다 (data-bound 로 한 번만)
    const hrSeats = () => { if (document.getElementById('hrProfileSeats')) import('./modules/hr-profile-seats.js').then((m) => m.setupHRProfileSeats()); };
    hrSeats();
    document.addEventListener('calendar:navigated', hrSeats);
    // 프로필 › 보안 설정 (2026-10-01): 내 인증 코드 · 패스키 관리 (data-bound 로 한 번만)
    const profileSecurity = () => { if (document.getElementById('secTotp')) import('./modules/profile-security.js').then((m) => m.setupProfileSecurity()); };
    profileSecurity();
    document.addEventListener('calendar:navigated', profileSecurity);
    // HR 상호작용 화면(보드·패널·캘린더·조직도)만 지연 로드 (spec 2026-09-07 §4.3)
    if (document.querySelector('#hrBoard,#hrTeamCal,#hrOrg,#hrRecList,#hrPanel')) import('./modules/hr-interactive.js').then((m) => m.setup());
    setupWork();
    setupWorkInbox();
    // 뷰 전환으로 처음 나타난 루트(#calGrid/#calList)에 모듈을 붙인다
    document.addEventListener('calendar:root-added', () => { setupCalendarGrid(); setupCalendarTimeGrid(); setupCalendarList(); setupCalendarBoard(); setupProjectList(); setupProjectTimeline(); });
    setupAppLauncher();
    initVaultSearch(); // no-ops on pages without #vaultUnifiedSearch
    initSelectPanels(); // no-ops on pages without [data-select-panel]
    initConfirmDialogs(); // in-app confirm for [data-confirm] forms + window.appConfirm
    setupModalBackdropGuard(); // 입력이 있는 모달은 바깥 클릭으로 닫지 않는다 (2026-09-30)
    setupFileViewer(); // 첨부 링크 a[data-file-view] 를 공용 보기 창에서 연다 (2026-10-01)
    initFormUX(); // double-submit guard + auto-dismissing success flashes
    initRailTooltips(); // 아이콘 레일에서 즉시 뜨는 사이드바 툴팁
    setupMobileShell(); // 모바일 셸: 「전체」 시트·탭 편집·공용 바텀 시트 (spec 2026-09-23)
    setupPwa(); // PWA: 서비스 워커·새 버전 알림·설치 안내 (spec 2026-09-24)
    setupBackLink(); // 교차 모듈 링크에 ?_from= 을 실어 되돌아가기를 가능하게 함
    setupGlobalSidebar(); // 전역 사이드바 아코디언 (사이드바 없는 화면에서는 no-op)
    setupOnboarding(); // 시작하기 카드 · 안내 카드 · 투어 (온보딩이 없는 화면에서는 no-op)
    runAdminPage(); // 관리자 화면의 페이지 모듈 (등록되지 않은 화면에서는 no-op)
    // 탭 틀 안의 탭이면 틀과 이어진다 (spec 2026-09-29) — 다른 setup 뒤라 첫 보고가 _from 을 지운 주소를 싣는다.
    setupWorkspaceFrame();
    // 페이지 컨테이너가 실제로 갈렸을 때만(= calendar:page-swapped) 새 DOM 에 다시 붙는다.
    // calendar:navigated 는 같은 경로의 부분 갱신에도 오므로 여기서는 쓰지 않는다.
    document.addEventListener('calendar:page-swapped', () => {
        // 모바일 드로어는 전체 새로고침이 없어졌으니 스스로 닫는다 (열린 채면 도착 화면을 가린다).
        // 사이드바 종류가 바뀌면 요소가 통째로 갈리므로, 살아 있는 것을 그때 찾아 닫는다.
        closeAdminDrawer();
        runAdminPage();
        initRailTooltips(); // 사이드바가 통째로 바뀐 경우에만 실제로 일한다 (data-rail-ready 가드)
        // 사이드바 종류가 바뀌면(전역 ↔ 관리자) syncSidebar 가 #adminSidebar 를
        // 통째로 갈아끼운다 (§3.7.3). 새 노드가 전역 사이드바면 아코디언을 다시
        // 붙인다 — data-bound 마커 덕에 갈리지 않은 경우에는 no-op 이다.
        // 관리자 사이드바에는 아코디언이 없으므로 initRailTooltips() 로 충분하다.
        setupGlobalSidebar();
        // 시작하기·안내 카드는 main.admin-main 안이라 SPA 교체로 사라졌다 다시 온다.
        setupOnboarding();
        initSelectPanels(document.querySelector('main.admin-main') || document);
        setupResponsiveTables();
    });

    // Expose client instance globally for debugging
    window.idpClient = client;
    window.idpUtils = utils;
});

// Touch optimization for mobile
if ('ontouchstart' in window) {
    document.addEventListener('touchstart', function() {}, { passive: true });
}
